import React, { useState, useContext, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import Utils from "Utils/utils";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { exportProcessingState } from "Components/popup/ExportProcessing";
import { exportEmailNotifyState } from "Components/popup/ExportEmailNotify";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import SequenceUtils from "Utils/SequenceUtils";
import ExportBtn from "Components/common/ExportBtn";
import PageLimit from "Components/common/PageLimit";
import ContactSelectedCount from "Components/common/ContactSelectedCount";
import Button from "Components/common/Button";
import { ReactComponent as FilterIcon } from "Assets/svg/filter.svg";
import { outboxStoreData } from "./outboxStore";
import { OutboxContext } from "./main";
import { ReactComponent as Downarrow } from "Assets/svg/donw-arrow.svg";
import { ReactComponent as EmailSubjectIcon } from "Assets/svg/outbox/emailSubject.svg";
import { ReactComponent as StarIcon } from "Assets/svg/star.svg";

export const FilterComponent = observer((props = {}) => {
  const { showUpdateBtns = false } = props || {}
  const {
    inputValues = {},
    handleStateChange = () => { },
    setInputValues = () => { },
    allEmailsSelectedInView = false,
    setAllEmailsSelectedInView = () => { },
    setAllEmailsSelectedCheckbox = () => { },
    getReloadOutboxCardData = () => { }
  } = useContext(OutboxContext);
  const [selectedCount, setSelectedCount] = useState(0);
  const [markReadUnreadDisabled, setMarkReadUnreadDisabled] = useState(false);
  const { name = "" } = outboxStoreData?.activeFilter;
  const { limit = 0, outboxData = {}, dataLoading = false } = inputValues || {};
  const { totalElements = 0 } = outboxData || {};
  const { result = [] } = outboxData || {};

  const handleLimit = (value = 10) => {
    outboxStoreData.setCurrentPage(1);
    handleStateChange("limit", value);
  };

  const limitObj = {
    limitPerPage: limit,
    text: "Showing",
    cbk: handleLimit,
    totalCount: totalElements
  };

  const getSelectedCount = () => {
    let count = 0;
    if (result?.length > 0) {
      count = ([...result] || []).reduce(
        (acc, item) => acc + (item?.isChecked ? 1 : 0),
        0
      );
    }
    setSelectedCount(count);
  };

  const exportCbk = () => {
    if (!allEmailsSelectedInView) {
      let tempEmailIds = [];
      if (result?.length > 0) {
        tempEmailIds =
          [...result]
            ?.filter(item => item?.isChecked)
            .map(item => item?.task?.id) || [];
      }
      handleExportAction(tempEmailIds);
    } else {
      handleExportAction([]);
    }
  };

  const constructExportPayload = (emailIds = []) => {
    if (!allEmailsSelectedInView) {
      return { outboxPayload: { ids: emailIds } };
    } else {
      let tempFilters = outboxStoreData.getFiltersPayload();
      return {
        outboxPayload: {
          ids: [],
          filters: { ...tempFilters, all: true }
        }
      };
    }
  };

  const clearSelectedLogs = (isClearLogs = false) => {
    setAllEmailsSelectedCheckbox(!isClearLogs);
    setAllEmailsSelectedInView(!isClearLogs);
    if (result?.length > 0) {
      setInputValues({
        ...inputValues,
        outboxData: {
          ...inputValues?.outboxData,
          result: inputValues?.outboxData?.result.map(item => ({
            ...item,
            isChecked: !isClearLogs
          }))
        }
      });
    }
  };

  const handleExportResponse = downloadLink => {
    if (!downloadLink) {
      exportProcessingState.setShowPopup(false);
      toasterState.setToastMsg(
        "Something went wrong. Please try again",
        "failure"
      );
    } else {
      exportProcessingState.completeAnimation();
      setTimeout(() => {
        Utils.downloadExportedContact(downloadLink);
        exportProcessingState.setShowPopup(false);
        setAllEmailsSelectedInView(false);
        clearSelectedLogs(true);
        toasterState.setToastMsg("Export is successfully completed", "success");
      }, 1000);
    }
  };

  const handleExportAction = async (emailIds = []) => {
    exportProcessingState.setShowPopup(true);
    const config = {
      url: URLS.exportOutboxEmails,
      method: "POST",
      data: constructExportPayload(emailIds)
    };
    let response = await makeApi(config);
    const { status = "", type = "", csvData = [] } = response?.data || {};
    const typeToCompare = "limit_reached";
    if (+status === 200 && type?.toLowerCase() === typeToCompare) {
      exportProcessingState.completeAnimation();
      exportProcessingState.setShowPopup(false);
      exportEmailNotifyState.setShowText("emails");
      exportEmailNotifyState.setShowPopup(true);
      setAllEmailsSelectedInView(false);
      clearSelectedLogs(true);
    } else if (+status === 200 && csvData?.length > 0) {
      Utils.exportCSVFile(
        [],
        csvData,
        "Salesgear-Inbox",
        handleExportResponse,
        true
      );
    } else {
      setAllEmailsSelectedInView(false);
      clearSelectedLogs(true);
      exportProcessingState.setShowPopup(false);
      toasterState.setToastMsg(
        "Something went wrong. Please try again",
        "failure"
      );
    }
  };

  const getSelectedTaskIds = () => {
    if (result?.length > 0) {
      return [...result]
        ?.filter(item => item?.isChecked)
        .map(item => item?.task?.id)
        .filter(id => id) || [];
    }
    return [];
  };

  const handleMarkAsRead = async () => {
    try {
      setMarkReadUnreadDisabled(true);
      if (!allEmailsSelectedInView) {
        const taskIds = getSelectedTaskIds();
        if (taskIds.length === 0) {
          toasterState.setToastMsg("Please select at least one email", "failure");
          return;
        }
        const totalOutboxTasks = taskIds.length;
        const response = await outboxStoreData.bulkMarkAsRead(
          taskIds,
          getReloadOutboxCardData,
          false,
          totalOutboxTasks
        );
        handleMarkAsReadResponse(response, taskIds.length);
      } else {
        const totalOutboxTasks = totalElements;
        const response = await outboxStoreData.bulkMarkAsRead(
          [],
          getReloadOutboxCardData,
          true,
          totalOutboxTasks
        );
        handleMarkAsReadResponse(response);
      }
    } finally {
      setMarkReadUnreadDisabled(false);
    }
  };

  const handleMarkAsReadResponse = (response, selectedCount = 0) => {
    if (!response) {
      const isSingle = selectedCount === 1 || selectedCount === 0;
      toasterState.setToastMsg(
        isSingle
          ? "Failed to mark message as read."
          : "Failed to mark messages as read. Please try again",
        "failure"
      );
      return;
    }

    const {
      success = false,
      bulkOperationResponse = {}
    } = response || {};

    if (!success) {
      const isSingle = selectedCount === 1 || selectedCount === 0;
      toasterState.setToastMsg(
        isSingle
          ? "Failed to mark message as read."
          : "Failed to mark messages as read. Please try again",
        "failure"
      );
      return;
    }

    const {
      activeJob = false,
      background = false,
      bulkOperationResult = {}
    } = bulkOperationResponse || {};

    if (activeJob) {
      clearSelectedLogs(true);
      SequenceUtils.showActiveJobPopupForBulkActions();
      return;
    }

    if (background) {
      confirmationPopupState.setPopupValues({
        content: "Marking messages as read. This may take a few minutes.",
        actionBtnText: "Ok",
        needCancelBtn: false,
        callback: () => confirmationPopupState.setShowPopup(false)
      });
      confirmationPopupState.setShowPopup(true);
      clearSelectedLogs(true);
      return;
    }

    const {
      total = 0,
      success: successCount = 0,
      failed = 0
    } = bulkOperationResult || {};

    const isSingle = total === 1 || selectedCount === 1;

    if (total > 0) {
      if (successCount > 0 && failed === 0) {
        if (isSingle) {
          toasterState.setToastMsg("Message marked as read.", "success");
        } else {
          toasterState.setToastMsg(
            `${successCount} message${successCount > 1 ? "s" : ""} marked as read.`,
            "success"
          );
        }
        clearSelectedLogs(true);
      } else if (successCount > 0 && failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `${successCount} message${successCount > 1 ? "s" : ""} marked as read. ${failed} failed.`,
          actionBtnText: "Ok",
          needCancelBtn: false,
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        clearSelectedLogs(true);
      } else if (failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `Failed to mark ${failed} message${failed > 1 ? "s" : ""} as read.`,
          actionBtnText: "Ok",
          needCancelBtn: false,
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        clearSelectedLogs(true);
      }
    } else {
      toasterState.setToastMsg(
        "No messages were updated. Please try again.",
        "failure"
      );
    }
  };

  const handleMarkAsUnread = async () => {
    try {
      setMarkReadUnreadDisabled(true);
      if (!allEmailsSelectedInView) {
        const taskIds = getSelectedTaskIds();
        if (taskIds.length === 0) {
          toasterState.setToastMsg("Please select at least one email", "failure");
          return;
        }
        const totalOutboxTasks = taskIds.length;
        const response = await outboxStoreData.bulkMarkAsUnread(
          taskIds,
          getReloadOutboxCardData,
          false,
          totalOutboxTasks
        );
        handleMarkAsUnreadResponse(response, taskIds.length);
      } else {
        const totalOutboxTasks = totalElements;
        const response = await outboxStoreData.bulkMarkAsUnread(
          [],
          getReloadOutboxCardData,
          true,
          totalOutboxTasks
        );
        handleMarkAsUnreadResponse(response);
      }
    } finally {
      setMarkReadUnreadDisabled(false);
    }
  };

  const handleMarkAsUnreadResponse = (response, selectedCount = 0) => {
    if (!response) {
      const isSingle = selectedCount === 1 || selectedCount === 0;
      toasterState.setToastMsg(
        isSingle
          ? "Failed to mark message as unread."
          : "Failed to mark messages as unread. Please try again",
        "failure"
      );
      return;
    }

    const {
      success = false,
      bulkOperationResponse = {}
    } = response || {};

    if (!success) {
      const isSingle = selectedCount === 1 || selectedCount === 0;
      toasterState.setToastMsg(
        isSingle
          ? "Failed to mark message as unread."
          : "Failed to mark messages as unread. Please try again",
        "failure"
      );
      return;
    }

    const {
      activeJob = false,
      background = false,
      bulkOperationResult = {}
    } = bulkOperationResponse || {};

    if (activeJob) {
      clearSelectedLogs(true);
      SequenceUtils.showActiveJobPopupForBulkActions();
      return;
    }

    if (background) {
      confirmationPopupState.setPopupValues({
        content: "Marking messages as unread. This may take a few minutes.",
        actionBtnText: "Ok",
        needCancelBtn: false,
        callback: () => confirmationPopupState.setShowPopup(false)
      });
      confirmationPopupState.setShowPopup(true);
      clearSelectedLogs(true);
      return;
    }

    const {
      total = 0,
      success: successCount = 0,
      failed = 0
    } = bulkOperationResult || {};

    const isSingle = total === 1 || selectedCount === 1;

    if (total > 0) {
      if (successCount > 0 && failed === 0) {
        if (isSingle) {
          toasterState.setToastMsg("Message marked as unread.", "success");
        } else {
          toasterState.setToastMsg(
            `${successCount} message${successCount > 1 ? "s" : ""} marked as unread.`,
            "success"
          );
        }
        clearSelectedLogs(true);
      } else if (successCount > 0 && failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `${successCount} message${successCount > 1 ? "s" : ""} marked as unread. ${failed} failed.`,
          actionBtnText: "Ok",
          needCancelBtn: false,
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        clearSelectedLogs(true);
      } else if (failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `Failed to mark ${failed} message${failed > 1 ? "s" : ""} as unread.`,
          actionBtnText: "Ok",
          needCancelBtn: false,
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        clearSelectedLogs(true);
      }
    } else {
      toasterState.setToastMsg(
        "No messages were updated. Please try again.",
        "failure"
      );
    }
  };

  useEffect(() => {
    getSelectedCount();
  }, [inputValues]);

  return (
    <div className="headerContents">
      <div className="leftHeaderContents">
        {showUpdateBtns && (
          <RepliesStatusDropdown />
        )}
        {name === "replied" && (
          <SentimentDropdown />
        )}
        {name === "bounced" && (
          <BounceTypeDropdown />
        )}
        {!dataLoading && selectedCount > 0 && (
          <ContactSelectedCount
            selectedCount={selectedCount}
            totalCount={totalElements}
            loading={dataLoading}
            allContactSelected={allEmailsSelectedInView}
            setAllContactSelected={setAllEmailsSelectedInView}
            resetCbk={() => clearSelectedLogs(true)}
            selectAllCbk={() => clearSelectedLogs(false)}
            selectedType="item"
          />
        )}
      </div>
      <div className="rightHeaderContents">
        {showUpdateBtns && (
          <>
            <Button
              id="markAsReadBtn"
              name="markAsReadBtn"
              btnText="Mark as read"
              className="markAsReadBtn"
              type="button"
              disabled={selectedCount === 0 || dataLoading || markReadUnreadDisabled}
              click={handleMarkAsRead}
            />
            <Button
              id="markAsUnreadBtn"
              name="markAsUnreadBtn"
              btnText="Mark as unread"
              className="markAsUnreadBtn"
              type="button"
              disabled={selectedCount === 0 || dataLoading || markReadUnreadDisabled}
              click={handleMarkAsUnread}
            />
          </>
        )}
        <PageLimit {...limitObj} />
        {!["all", "scheduled"].includes(name?.toLowerCase()) && (
          <ExportBtn
            selectedEntityCount={selectedCount || 0}
            cbk={exportCbk}
            loading={dataLoading}
            isExportEnabled={true}
            checked={selectedCount > 0}
            pageType="inbox"
            tooltipText="emails"
          />
        )}
        <FilterButton />
      </div>
    </div>
  );
});

const RepliesStatusDropdown = observer(() => {
  const { repliesStats = {}, repliesStatsLoading = false } = outboxStoreData || {};

  const options = [
    { label: "Read Replies", id: "READ", count: repliesStats.readCount },
    { label: "Unread Replies", id: "UNREAD", count: repliesStats.unreadCount }
  ];

  const handleRepliesChange = (values) => {
    let finalSubstatus = "ALL";
    if (values.includes("READ") && values.includes("UNREAD")) {
      finalSubstatus = "ALL";
    } else if (values.includes("READ")) {
      finalSubstatus = "READ";
    } else if (values.includes("UNREAD")) {
      finalSubstatus = "UNREAD";
    } else {
      // Default to ALL if nothing selected
      finalSubstatus = "ALL";
    }
    outboxStoreData.setRepliedSubstatus(finalSubstatus);
  };

  const getSelectedValues = () => {
    const sub = outboxStoreData.repliedSubstatus;
    if (sub === "ALL") return ["READ", "UNREAD"];
    return [sub];
  };

  const getTriggerLabel = () => {
    const sub = outboxStoreData.repliedSubstatus;
    if (sub === "ALL") return "All Replies";
    if (sub === "READ") return "Read Replies";
    if (sub === "UNREAD") return "Unread Replies";
    return "All Replies";
  };

  const getTriggerCount = () => {
    const sub = outboxStoreData.repliedSubstatus;
    if (sub === "ALL") return repliesStats.allCount;
    if (sub === "READ") return repliesStats.readCount;
    if (sub === "UNREAD") return repliesStats.unreadCount;
    return repliesStats.allCount;
  };

  return (
    <BaseFilterDropdown
      title="Replies"
      options={options}
      selectedValues={getSelectedValues()}
      onChange={handleRepliesChange}
      triggerLabel={getTriggerLabel()}
      triggerCount={getTriggerCount()}
      showImportant={true}
      isImportant={outboxStoreData.isImportant}
      onImportantChange={(val) => outboxStoreData.setIsImportant(val)}
      statsLoading={repliesStatsLoading}
    />
  );
});

const BaseFilterDropdown = ({
  title,
  options = [],
  selectedValues = [],
  onChange = () => { },
  triggerLabel,
  triggerCount,
  icon: HeaderIcon,
  showImportant = false,
  isImportant = false,
  onImportantChange = () => { },
  statsLoading = false
}) => {
  const { getReloadOutboxCardData = () => { } } = useContext(OutboxContext);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [localSelectedValues, setLocalSelectedValues] = useState([]);
  const [localIsImportant, setLocalIsImportant] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalSelectedValues([...selectedValues]);
      setLocalIsImportant(isImportant);
    }
  }, [isOpen]);

  const toggleDropdown = () => setIsOpen(prev => !prev);

  const isAllActive = options.length > 0 && options.every(opt => localSelectedValues.includes(opt.id));

  const handleAllToggle = () => {
    if (!isAllActive) {
      // Only allow selecting all. Turning OFF is not allowed.
      setLocalSelectedValues(options.map(opt => opt.id));
    }
  };

  const handleCheckboxChange = (optionId) => {
    setLocalSelectedValues(prev => {
      if (prev.includes(optionId)) {
        const next = prev.filter(id => id !== optionId);
        // Ensure at least one is checked if required?
        // For replies status, it defaults to something. For others, maybe empty is okay.
        return next;
      } else {
        return [...prev, optionId];
      }
    });
  };

  const handleImportantToggle = () => {
    setLocalIsImportant(!localIsImportant);
  };

  const handleSearch = () => {
    outboxStoreData.setCurrentPage(1);
    onChange(localSelectedValues);
    if (showImportant) {
      onImportantChange(localIsImportant);
    }
    setIsOpen(false);
    getReloadOutboxCardData();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="repliedStatusDropdown" ref={dropdownRef}>
      <div className="repliedStatusDropdownControl">
        <button
          type="button"
          className={`repliedStatusDropdownTrigger ${isOpen ? "dropdownOpen" : ""}`}
          onClick={toggleDropdown}
        >
          <span className="repliedStatusDropdownText">
            {triggerLabel || title}
            {statsLoading ? (
              <span className="repliedStatusDropdownCountLoader linear-background"></span>
            ) : (
              <span className="repliedStatusDropdownCount">
                {triggerCount || 0}
              </span>
            )}
          </span>
          <span className="repliedStatusDropdownIcon">
            <Downarrow />
          </span>
        </button>
        {isOpen && (
          <div className="repliedStatusDropdownMenu">
            {/* All - Toggle */}
            <div className="repliedStatusDropdownItem repliedStatusDropdownItemWithToggle">
              <label className="repliedStatusToggleLabel">
                <input
                  type="checkbox"
                  className="repliedStatusToggleInput"
                  checked={isAllActive}
                  onChange={handleAllToggle}
                />
                <span className="repliedStatusToggleSwitch"></span>
                <span className="repliedStatusDropdownItemIcon">
                  {HeaderIcon ? <HeaderIcon /> : <EmailSubjectIcon />}
                </span>
                <span className="repliedStatusDropdownItemLabel">
                  All {title}
                </span>
              </label>
            </div>

            {/* Options - Checkboxes */}
            {options.map((option) => (
              <div key={option.id} className="repliedStatusDropdownItem repliedStatusDropdownItemWithCheckbox">
                <label className="repliedStatusCheckboxLabel">
                  <input
                    type="checkbox"
                    className="repliedStatusCheckboxInput"
                    checked={localSelectedValues.includes(option.id)}
                    onChange={() => handleCheckboxChange(option.id)}
                  />
                  <span className="repliedStatusCheckbox"></span>
                  <span className="repliedStatusDropdownItemLabel">
                    {option.label}
                    {statsLoading ? (
                      <span className="repliedStatusDropdownItemCountLoader linear-background"></span>
                    ) : (
                      <span className="repliedStatusDropdownItemCount">
                        ({option.count || 0})
                      </span>
                    )}
                  </span>
                </label>
              </div>
            ))}

            {/* Important - Toggle */}
            {showImportant && (
              <div className="repliedStatusDropdownItem repliedStatusDropdownItemWithToggle">
                <label className="repliedStatusToggleLabel">
                  <input
                    type="checkbox"
                    className="repliedStatusToggleInput"
                    checked={localIsImportant}
                    onChange={handleImportantToggle}
                  />
                  <span className="repliedStatusToggleSwitch"></span>
                  <span className="repliedStatusDropdownItemIcon">
                    <StarIcon />
                  </span>
                  <span className="repliedStatusDropdownItemLabel">
                    Important
                  </span>
                </label>
              </div>
            )}

            {/* Search Button */}
            <div className="repliedStatusDropdownSearchContainer">
              <button
                type="button"
                className={`repliedStatusDropdownSearchButton ${localSelectedValues.length === 0 && (!showImportant || !localIsImportant) ? "disabled" : ""}`}
                onClick={handleSearch}
                disabled={localSelectedValues.length === 0 && (!showImportant || !localIsImportant)}
              >
                Search
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SentimentDropdown = observer(() => {
  const { repliesStats = {}, repliesStatsLoading = false } = outboxStoreData || {};

  const options = [
    { label: "Positive", id: "positive", count: repliesStats.positiveCount },
    { label: "Negative", id: "negative", count: repliesStats.negativeCount },
    { label: "Neutral", id: "neutral", count: repliesStats.neutralCount },
    { label: "Unknown", id: "unknown", count: repliesStats.unknownCount }
  ];

  const handleSentimentChange = (values) => {
    outboxStoreData.setSelectedSentiments(values);
  };

  const getTriggerLabel = () => {
    if (outboxStoreData.selectedSentiments.length === 0) return "Sentiments";
    if (outboxStoreData.selectedSentiments.length === 1) {
      const opt = options.find(o => o.id === outboxStoreData.selectedSentiments[0]);
      return opt ? opt.label : "Sentiments";
    }
    return "Sentiments";
  };

  const getTriggerCount = () => {
    if (outboxStoreData.selectedSentiments.length === 0) return repliesStats.allCount;
    return outboxStoreData.selectedSentiments.reduce((acc, id) => {
      const opt = options.find(o => o.id === id);
      return acc + (opt ? opt.count : 0);
    }, 0);
  };

  return (
    <BaseFilterDropdown
      title="Sentiments"
      options={options}
      selectedValues={outboxStoreData.selectedSentiments}
      onChange={handleSentimentChange}
      triggerLabel={getTriggerLabel()}
      triggerCount={getTriggerCount()}
      statsLoading={repliesStatsLoading}
    />
  );
});

const BounceTypeDropdown = observer(() => {
  const { repliesStats = {}, repliesStatsLoading = false } = outboxStoreData || {};

  const options = [
    { label: "Hard Bounce", id: "hard", count: repliesStats.hardBounceCount },
    { label: "Soft Bounce", id: "soft", count: repliesStats.softBounceCount }
  ];

  const handleBounceTypeChange = (values) => {
    outboxStoreData.setSelectedBounceTypes(values);
  };

  const getTriggerLabel = () => {
    if (outboxStoreData.selectedBounceTypes.length === 0) return "Bounce Types";
    if (outboxStoreData.selectedBounceTypes.length === 1) {
      const opt = options.find(o => o.id === outboxStoreData.selectedBounceTypes[0]);
      return opt ? opt.label : "Bounce Types";
    }
    return "Bounce Types";
  };

  const getTriggerCount = () => {
    const { bounceCount = 0 } = outboxStoreData.filterObject || {};
    if (outboxStoreData.selectedBounceTypes.length === 0) return bounceCount;
    return outboxStoreData.selectedBounceTypes.reduce((acc, id) => {
      const opt = options.find(o => o.id === id);
      return acc + (opt ? opt.count : 0);
    }, 0);
  };

  return (
    <BaseFilterDropdown
      title="Bounce Types"
      options={options}
      selectedValues={outboxStoreData.selectedBounceTypes}
      onChange={handleBounceTypeChange}
      triggerLabel={getTriggerLabel()}
      triggerCount={getTriggerCount()}
      statsLoading={repliesStatsLoading}
    />
  );
});

const FilterButton = () => {
  const {
    inputValues = {},
    showFilter = false,
    setShowFilter = () => { }
  } = useContext(OutboxContext);
  const { filterLoading = false, dataLoading = false } = inputValues || {};

  let isClickable = !filterLoading && !dataLoading;

  const handleClick = () => {
    isClickable && setShowFilter(!showFilter);
  };

  return (
    <div
      className={`outboxFilterIconToggle ${isClickable ? "" : "disabled"} ${outboxStoreData?.tempFilterList?.length > 0 ? "filterUsed" : ""
        }`}
      onClick={handleClick}
    >
      <FilterIcon />
      <span className="text">Filter</span>
      {outboxStoreData?.tempFilterList?.length > 0 && (
        <span className="count">{outboxStoreData?.tempFilterList?.length}</span>
      )}
    </div>
  );
};

export default FilterComponent;
